import React, { useEffect, useState } from 'react';
import * as api from '../../api/api';
import GoalEditor from './GoalEditor';
import './GoalsEditor.css';

const GoalsEditor = ({ setGoalsLoading }) => {
    const [goals, setGoals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [unitSettings, setUnitSettings] = useState({ distance_unit: 'KILOMETRES', elevation_unit: 'METRES' });
    // Removed settingsLoading state
    const [settingsError, setSettingsError] = useState(null);
    const [creationMessage, setCreationMessage] = useState(null);
    const [recentlyCreatedGoalId, setRecentlyCreatedGoalId] = useState(null);
    const [isAddingGoal, setIsAddingGoal] = useState(false);

    useEffect(() => {
        if (!creationMessage) {
            return undefined;
        }
        const messageTimer = setTimeout(() => setCreationMessage(null), 10000);
        return () => clearTimeout(messageTimer);
    }, [creationMessage]);

    const updateGoals = () => {
        setLoading(true);
        setError(null);
        if (setGoalsLoading) setGoalsLoading(true);
        api.getGoals()
            .then((goals) => {
                setGoals(
                    goals.data.sort((a, b) => {
                        const aEndDateEpoch = new Date(a.endDate);
                        const aStartDateEpoch = new Date(a.startDate);
                        const bEndDateEpoch = new Date(b.endDate);
                        const bStartDateEpoch = new Date(b.startDate);
                        return aEndDateEpoch === bEndDateEpoch ? 
                            (aStartDateEpoch > bStartDateEpoch ? -1 : 1) : 
                            (aEndDateEpoch > bEndDateEpoch ? -1 : 1);
                    })
                );
                setLoading(false);
                if (setGoalsLoading) setGoalsLoading(false);
            })
            .catch((e) => {
                setError(api.getErrorMessage(e, 'Failed to load goals'));
                setLoading(false);
                if (setGoalsLoading) setGoalsLoading(false);
            });
    };

    useEffect(() => {
        api.getUserSettings()
            .then(data => {
                setUnitSettings({
                    distance_unit: data.distance_unit || 'KILOMETRES',
                    elevation_unit: data.elevation_unit || 'METRES',
                });
                // removed setSettingsLoading
                updateGoals();
            })
            .catch((e) => {
                setSettingsError(api.getErrorMessage(e, 'Failed to load user settings'));
                // removed setSettingsLoading
            });
        // eslint-disable-next-line
    }, []);

    return (
        <main className="goals-editor-page">
            {settingsError && (
                <div className="goals-message goals-message-error">
                    {settingsError}
                </div>
            )}
            {!settingsError && (
                <>
                    <div className="goals-editor-header">
                        <div>
                            <h3>Edit Your Goals</h3>
                            <p className="goals-editor-intro">Set targets and choose which activities count towards them.</p>
                        </div>
                        <button
                            className="add-goal-button"
                            disabled={isAddingGoal}
                            onClick={() => {
                                setError(null);
                                setCreationMessage(null);
                                setIsAddingGoal(true);
                            }}
                        >
                            <span aria-hidden="true">{isAddingGoal ? '...' : '+'}</span> {isAddingGoal ? 'New goal' : 'Add goal'}
                        </button>
                    </div>
                    {creationMessage && (
                        <div className={`goals-message goals-message-${creationMessage.type}`} role="status">
                            {creationMessage.text}
                        </div>
                    )}
                    {loading && (
                        <div className="goals-message goals-message-loading">
                            Loading user goals...
                        </div>
                    )}
                    {error && (
                        <div className="goals-message goals-message-error">
                            {error}
                        </div>
                    )}
                    <div className="goals-list">
                        {isAddingGoal && (
                            <div className="new-goal-card">
                                <span className="new-goal-badge">Draft</span>
                                <GoalEditor
                                    goal={{
                                        goalId: 'new',
                                        goalName: '',
                                        activityTypes: [],
                                        endDate: new Date(new Date().getFullYear() + 1, 0, 1).toISOString(),
                                        goalUnit: 'DISTANCE',
                                        goalValue: 0,
                                        startDate: new Date(new Date().getFullYear(), 0, 1).toISOString(),
                                        includeInPlan: true,
                                    }}
                                    isNew
                                    onSave={(goalName) => {
                                        setIsAddingGoal(false);
                                        setCreationMessage({ type: 'added', text: `Goal added: ${goalName}.` });
                                        updateGoals();
                                    }}
                                    onCancel={() => setIsAddingGoal(false)}
                                    unitSettings={unitSettings}
                                />
                            </div>
                        )}
                        {goals != null && goals.map((goal) => (
                            <div key={goal.goalId} className={goal.goalId === recentlyCreatedGoalId ? 'new-goal-card' : ''}>
                                {goal.goalId === recentlyCreatedGoalId && <span className="new-goal-badge">New</span>}
                                <GoalEditor
                                    goal={goal}
                                    deleteGoal={() => {
                                        setError(null);
                                        api.deleteGoal(goal.goalId).then(() => {
                                            setCreationMessage({ type: 'deleted', text: `Goal deleted: ${goal.goalName}.` });
                                            updateGoals();
                                        }).catch((e) => {
                                            setError(api.getErrorMessage(e, 'Unable to delete this goal.'));
                                            // The goal may already be gone server-side (stale UI, another
                                            // tab) - refresh the list either way so it stops pretending to
                                            // still be there.
                                            updateGoals();
                                        });
                                    }}
                                    onSave={(message, goalName) => setCreationMessage({ type: 'updated', text: `${message.replace('Goal updated.', `Goal updated: ${goalName}.`)}` })}
                                    unitSettings={unitSettings}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </main>
    );

}

export default GoalsEditor;
